/**
 * A `[picture N]` line of a support message, as the editor shows it: the
 * picture itself, not the line.
 *
 * Its text is still the line, so the editor's text is the message body
 * exactly — the node only changes what the line looks like while writing.
 */

import Skeleton from "@mui/material/Skeleton";
import {
	$applyNodeReplacement,
	$createLineBreakNode,
	$isLineBreakNode,
	DecoratorNode,
	type LexicalNode,
	type NodeKey,
	type SerializedLexicalNode,
	type Spread,
} from "lexical";
import { createContext, type JSX, useContext } from "react";
import type { DraftPicture } from "./support-draft-pictures";
import { pictureNumber } from "./support-picture-tokens";
import { SupportReplyPicture } from "./support-reply-pictures";

/** What a picture in the text needs from the composer around it. */
export interface ComposerPictures {
	pictures: DraftPicture[];
	onEdit: (key: number) => void;
	onRemove: (key: number) => void;
	compact: boolean;
}

export const ComposerPicturesContext = createContext<ComposerPictures | null>(null);

/** The class the editable styles a picture's line box by. */
export const PICTURE_CLASS = "support-composer-picture";

const ComposerPicture: React.FC<{ number: number }> = ({ number }) => {
	const context = useContext(ComposerPicturesContext);
	if (!context)
		throw new Error("A picture in the text renders inside SupportComposer");
	const picture = context.pictures[number - 1];
	// A draft inserted into the reply brings its text first and fetches its
	// pictures after, one at a time: the line is there before its picture.
	if (!picture) {
		return (
			<Skeleton
				variant="rounded"
				component="span"
				sx={{ display: "inline-block" }}
				width={context.compact ? 84 : 135}
				height={context.compact ? 56 : 90}
				data-component="support-reply-picture-loading"
			/>
		);
	}
	return (
		<SupportReplyPicture
			picture={picture}
			onEdit={context.onEdit}
			onRemove={context.onRemove}
			compact={context.compact}
		/>
	);
};

type SerializedPictureNode = Spread<{ line: string }, SerializedLexicalNode>;

export class SupportPictureNode extends DecoratorNode<JSX.Element> {
	/** The body's line, kept as written so the text round-trips exactly. */
	__line: string;

	static getType(): string {
		return "support-picture";
	}

	static clone(node: SupportPictureNode): SupportPictureNode {
		return new SupportPictureNode(node.__line, node.__key);
	}

	static importJSON(json: SerializedPictureNode): SupportPictureNode {
		return $createSupportPictureNode(json.line);
	}

	constructor(line: string, key?: NodeKey) {
		super(key);
		this.__line = line;
	}

	exportJSON(): SerializedPictureNode {
		return { ...super.exportJSON(), line: this.getLatest().__line };
	}

	createDOM(): HTMLElement {
		const span = document.createElement("span");
		span.className = PICTURE_CLASS;
		return span;
	}

	updateDOM(): false {
		return false;
	}

	getTextContent(): string {
		return this.getLatest().__line;
	}

	/** 1-based, into the composer's `pictures`. */
	getNumber(): number {
		const number = pictureNumber(this.getLatest().__line);
		if (number === null) throw new Error(`Not a picture line: ${this.__line}`);
		return number;
	}

	decorate(): JSX.Element {
		return <ComposerPicture number={this.getNumber()} />;
	}
}

export const $createSupportPictureNode = (line: string): SupportPictureNode =>
	$applyNodeReplacement(new SupportPictureNode(line));

export const $isSupportPictureNode = (
	node: LexicalNode | null | undefined
): node is SupportPictureNode => node instanceof SupportPictureNode;

/**
 * A picture is a line of its own, or its line stops being `[picture N]`.
 * Typing straight after one, or joining the line under it, would put text on
 * that line — so whatever lands beside a picture is moved to the next line.
 */
export const $keepPictureOnItsLine = (node: SupportPictureNode): void => {
	const previous = node.getPreviousSibling();
	if (previous && !$isLineBreakNode(previous))
		node.insertBefore($createLineBreakNode());
	const next = node.getNextSibling();
	if (next && !$isLineBreakNode(next)) node.insertAfter($createLineBreakNode());
};
